/**
 * Converts an array of objects to CSV format
 * @param data Array of objects to convert
 * @param headers Array of header names (in order)
 * @returns CSV string
 */
export function convertToCSV<T extends Record<string, any>>(
  data: T[],
  headers: { key: keyof T; label: string }[]
): string {
  if (data.length === 0) return '';

  // Helper function to escape CSV field values
  const escapeCSVField = (value: any): string => {
    if (value === null || value === undefined) return '';

    const stringValue = String(value);

    // Check if field needs quoting (contains comma, quote, or newline)
    if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
      // Double any quotes and wrap in quotes
      return `"${stringValue.replace(/"/g, '""')}"`;
    }

    return stringValue;
  };

  // Create header row
  const headerRow = headers.map(h => escapeCSVField(h.label)).join(',');

  // Create data rows
  const dataRows = data.map(row => {
    return headers.map(header => escapeCSVField(row[header.key])).join(',');
  });

  // Combine header and data rows
  return [headerRow, ...dataRows].join('\n');
}

/**
 * Downloads a CSV file to the user's computer
 * @param csvContent The CSV string content
 * @param filename The desired filename (without .csv extension)
 */
export function downloadCSV(csvContent: string, filename: string): void {
  // Create a Blob from the CSV content
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

  // Create a temporary download link
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  link.style.visibility = 'hidden';

  // Trigger download
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Generates a filename with date range for exports
 * @param baseName Base name for the file (e.g., student name or class name)
 * @param startDate Start date string
 * @param endDate End date string
 * @returns Formatted filename without extension
 */
export function generateFilename(
  baseName: string,
  startDate: string,
  endDate: string
): string {
  // Sanitize base name by removing special characters
  const sanitized = baseName.replace(/[^a-zA-Z0-9-_ ]/g, '').trim();

  return `${sanitized}_${startDate}_to_${endDate}`;
}
